import Money from "@components/common/Money";
import { TipsOne } from "@icon-park/svg";
import { formatMoney, formatNumber, formatPercent } from "@justhome/common";
import { MaximumBudget, MaximumBudgetResult } from "@justhome/common/api/core";
import {
  Button,
  Icon,
  InfoIcon,
  InlineNotification,
  Table,
  Title,
} from "@justhome/react-ui";
import useLocale from "@lib/hooks/useLocale";
import usePartnerContext from "@lib/hooks/usePartnerContext";
import { getMeetingPath, getPartnerMeetingPath } from "@lib/routing";
import clsx from "clsx";
import { useTranslation } from "next-i18next";
import { FC } from "react";
import MaximumBudgetEditModal from "../MaximumBudgetEditModal";
import MaximumBudgetResultRow from "./MaximumBudgetCardRow";

export interface MaximumBudgetCardProps {
  maximumBudget: MaximumBudget;
  className?: string;
}

const MaximumBudgetCard: FC<MaximumBudgetCardProps> = ({
  maximumBudget,
  className,
}) => {
  const { t } = useTranslation("budget-check");
  const locale = useLocale();
  const { partner } = usePartnerContext();

  const noResult =
    !maximumBudget.result?.maxPurchasePrice ||
    maximumBudget.result?.maxPurchasePrice <= 0;

  let maximumBudgetLower = 0;
  let maximumBudgetUpper = 0;
  if (maximumBudget.result?.maxPurchasePrice) {
    maximumBudgetLower =
      Math.floor((maximumBudget.result.maxPurchasePrice * 0.9) / 1000) * 1000;
    maximumBudgetUpper = maximumBudget.result.maxPurchasePrice;
  }

  return (
    <section
      className={clsx(
        "border border-neutral-50 bg-neutral-0 shadow-4 rounded-2xs",
        className,
      )}
    >
      <div className="p-s m:p-3s flex flex-col items-center">
        {!noResult ? (
          <>
            <Title as="h3" className="text-h3 mb-3s text-balance">
              {t("maximum_budget_title")}
            </Title>
            <div className="flex gap-3xs items-center mb-2s">
              <p className="text-h1 font-book text-primary-800">
                {formatNumber(maximumBudgetLower)} -{" "}
                <Money value={maximumBudgetUpper} />
              </p>
              <InfoIcon size="lg">
                <MaximumBudgetCardResultExplanation {...maximumBudget} />
              </InfoIcon>
            </div>
            <div className="flex flex-col s:flex-row w-full justify-stretch gap-2xs m:gap-none m:divide-x divide-neutral-50">
              <MaximumBudgetResultRow
                label={t("result_equity_label")}
                value={<Money value={maximumBudget.equity} />}
              />
              <MaximumBudgetResultRow
                label={t("result_loan_amount_label")}
                value={<Money value={maximumBudget.result?.loanAmount} />}
              />
              <MaximumBudgetResultRow
                label={t("result_ancillary_cost_label")}
                info={<PurchaseCostInfo result={maximumBudget.result!} />}
                value={<Money value={maximumBudget.result?.ancillaryCosts} />}
              />
            </div>
          </>
        ) : (
          <InlineNotification
            size="sm"
            type="info"
            icon={<Icon icon={TipsOne} size="24" />}
            className="text-body-2 mt-m flex text-info-700"
          >
            {t("zero_result_title")}
          </InlineNotification>
        )}
        {partner ? (
          <div className="flex flex-col justify-center items-center gap-s mt-m">
            <Button
              size="md"
              variant="primary"
              href={
                partner
                  ? getPartnerMeetingPath(locale, partner.id)
                  : getMeetingPath(locale)
              }
              className="mb-2xs"
              iconEnd="Right"
            >
              {t("secondary_cta_label")}
            </Button>
          </div>
        ) : (
          <>
            <div className="flex flex-row gap-m mt-m">
              <Button
                size="md"
                as="a"
                target="_blank"
                href="/vergleich"
                className="mb-2xs"
                iconEnd="Right"
              >
                {t("cta_label")}
              </Button>
              {!maximumBudget.hasSpecificProperty && (
                <Button
                  size="md"
                  variant="primary"
                  href={getMeetingPath(locale)}
                  className="mb-2xs"
                  iconEnd="Right"
                >
                  {t("secondary_cta_label")}
                </Button>
              )}
            </div>
            <p className="text-title-1 text-neutral-675 text-opacity-60">
              {t("result_button_hint")}
            </p>
          </>
        )}
      </div>
      <MaximumBudgetEditModal />
    </section>
  );
};

export default MaximumBudgetCard;

const PurchaseCostInfo = ({ result }: { result: MaximumBudgetResult }) => {
  const { t } = useTranslation("budget-check");
  const _locale = useLocale();
  const locale = _locale === "en" ? "en" : "de";

  const costs = [
    {
      name: `Grunderwerbssteuer (${formatPercent(
        (result.propertyTransferTax / result.maxPurchasePrice) * 100,
        locale,
      )})`,
      amount: result.propertyTransferTax,
    },
    {
      name: `Notarkosten (${formatPercent(
        (result.notaryFee / result.maxPurchasePrice) * 100,
        locale,
      )})`,
      amount: result.notaryFee,
    },
    {
      name: `Maklerprovision (${formatPercent(
        (result.realEstateAgentFee / result.maxPurchasePrice) * 100,
        locale,
      )})`,
      amount: result.realEstateAgentFee,
    },
  ];

  return (
    <div className="flex flex-col gap-s">
      <p>{t("result_ancillary_cost_info")}</p>
      <Table totalRows={3}>
        <Table.Body>
          {costs.map((cost, index) => (
            <Table.Row key={index}>
              <Table.Cell>{cost.name}</Table.Cell>
              <Table.Cell className="text-right">
                {formatMoney(cost.amount, true, locale)}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  );
};

const MaximumBudgetCardResultExplanation = ({
  equity,
  result,
}: MaximumBudget) => {
  const { t } = useTranslation("budget-check");
  const _locale = useLocale();
  const locale = _locale === "en" ? "en" : "de";

  const costs = [
    {
      name: `Eigenkapital`,
      amount: equity,
    },
    {
      name: `Maximales Darlehen`,
      amount: result!.loanAmount,
      isPositive: true,
    },

    {
      name: `Kaufnebenkosten`,
      amount: result!.ancillaryCosts,
      isNegative: true,
    },
  ];

  return (
    <div className="flex flex-col gap-s">
      <p>{t("result_max_purchase_price_info")}</p>
      <Table totalRows={3}>
        <Table.Body>
          {costs.map((cost, index) => (
            <Table.Row key={index}>
              <Table.Cell>{cost.name}</Table.Cell>
              <Table.Cell
                className={clsx(
                  "text-center",
                  cost.isNegative && "text-error-625",
                  cost.isPositive && "text-success-625",
                )}
              >
                {cost.isNegative && "-"}
                {cost.isPositive && "+"}
              </Table.Cell>
              <Table.Cell
                className={clsx(
                  "text-right",
                  cost.isNegative && "text-error-625",
                  cost.isPositive && "text-success-625",
                )}
              >
                {formatMoney(cost.amount, true, locale)}
              </Table.Cell>
            </Table.Row>
          ))}
          <Table.Row className="w-full h-xs"></Table.Row>
          <Table.Row>
            <Table.Cell>Maximales Kaufbudget</Table.Cell>
            <Table.Cell></Table.Cell>
            <Table.Cell className="text-right">
              {formatMoney(result!.maxPurchasePrice, true, locale)}
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
    </div>
  );
};
